<template>
  <div class="report-management-container">
    <div class="page-title">填报管理</div>
    
    <!-- 搜索筛选区域 -->
    <div class="filter-section">
      <div class="filter-row">
        <el-input placeholder="搜索模板名称" v-model="filters.name" class="filter-input">
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-input>
        <el-button type="primary" plain @click="handleSearch">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
        <div class="spacer"></div>
        <el-button type="primary" icon="el-icon-plus" @click="handleAddReport">新增填报</el-button>
      </div>
    </div>
    
    <!-- 数据表格 -->
    <el-table :data="tableData" border stripe style="width: 100%" v-loading="loading">
      <el-table-column prop="name" label="模板名称" min-width="200" align="center"></el-table-column>
      <el-table-column prop="healthRatingName" label="健康评级" width="100" align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.healthRatingName }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="classNames" label="适用班组" min-width="200" align="center"></el-table-column>
      <el-table-column prop="fillTypeName" label="填报方式" width="100" align="center"></el-table-column>
      <el-table-column prop="statusName" label="启用/禁用" width="100" align="center">
        <template slot-scope="scope">
          <span :class="{ 'status-enabled': scope.row.status === 1, 'status-disabled': scope.row.status === 2 }">
            {{ scope.row.statusName }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="createUserName" label="创建人" width="100" align="center"></el-table-column>
      <el-table-column label="创建时间" width="180" align="center">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="updateUserName" label="最后修改人" width="100" align="center"></el-table-column>
      <el-table-column label="最后修改时间" width="180" align="center">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.updateTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button type="text" @click="handleToggleStatus(scope.row)">{{ scope.row.status === 1 ? '禁用' : '启用' }}</el-button>
          <el-button type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total">
      </el-pagination>
    </div>
    
    <!-- 新增/编辑填报模板弹窗 -->
    <el-dialog
      :title="dialogType === 'add' ? '新增填报模板' : '编辑填报模板'"
      :visible.sync="dialogVisible"
      width="50%"
      :before-close="handleCloseDialog">
      <el-form :model="formData" label-width="100px" :rules="formRules" ref="reportForm">
        <el-form-item label="模板名称" prop="templateName">
          <el-input v-model="formData.templateName" placeholder="请输入模板名称"></el-input>
        </el-form-item>
        <el-form-item label="健康评级" prop="healthStatus">
          <el-select v-model="formData.healthStatus" placeholder="请选择健康评级" style="width: 100%">
            <el-option label="正常" value="正常"></el-option>
            <el-option label="异常" value="异常"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="适用班组" prop="applicableTeams">
          <el-select v-model="formData.applicableTeams" multiple placeholder="请选择适用班组" style="width: 100%">
            <el-option label="生产一班" value="生产一班"></el-option>
            <el-option label="生产二班" value="生产二班"></el-option>
            <el-option label="生产三班" value="生产三班"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="填报方式" prop="reportMethod">
          <el-select v-model="formData.reportMethod" placeholder="请选择填报方式" style="width: 100%">
            <el-option label="按天" value="按天"></el-option>
            <el-option label="按周" value="按周"></el-option>
            <el-option label="按月" value="按月"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="formData.status">
            <el-radio label="启用">启用</el-radio>
            <el-radio label="禁用">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
    
    <!-- 删除确认弹窗 -->
    <el-dialog
      title="确认删除"
      :visible.sync="deleteDialogVisible"
      width="30%">
      <div>确定要删除该填报模板吗？此操作不可恢复。</div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmDelete">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { searchReportTemplates, deleteReportTemplate, updateReportTemplateStatus } from '@/api/report'

export default {
  name: 'ReportManagement',
  data() {
    return {
      // 筛选条件
      filters: {
        name: '',
        pageNum: 1,
        pageSize: 10
      },
      
      // 表格数据
      tableData: [],
      
      // 分页数据
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      
      // 弹窗相关
      dialogVisible: false,
      dialogType: 'add', // 'add' 或 'edit'
      formData: {
        id: null,
        templateName: '',
        healthStatus: '正常',
        applicableTeams: [],
        reportMethod: '按天',
        status: '启用'
      },
      formRules: {
        templateName: [
          { required: true, message: '请输入模板名称', trigger: 'blur' }
        ],
        healthStatus: [
          { required: true, message: '请选择健康评级', trigger: 'change' }
        ],
        applicableTeams: [
          { required: true, message: '请选择适用班组', trigger: 'change' }
        ],
        reportMethod: [
          { required: true, message: '请选择填报方式', trigger: 'change' }
        ]
      },
      
      // 删除弹窗
      deleteDialogVisible: false,
      currentDeleteId: null,
      loading: false
    }
  },
  created() {
    // 加载填报模板数据
    this.fetchReportTemplates()
  },
  methods: {
    // 日期时间格式化函数
    formatDateTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    // 获取填报模板列表
    fetchReportTemplates() {
      this.loading = true
      const params = {
        name: this.filters.name,
        pageNum: this.pagination.currentPage,
        pageSize: this.pagination.pageSize
      }
      
      searchReportTemplates(params)
        .then(response => {
          if (response.code === 200) {
            const { list, total } = response.data
            // 处理数据，添加索引
            this.tableData = list.map((item, index) => {
              return {
                ...item,
                index: (this.pagination.currentPage - 1) * this.pagination.pageSize + index + 1
              }
            })
            this.pagination.total = total
          } else {
            this.$message.error(response.message || '获取填报模板列表失败')
            this.tableData = []
            this.pagination.total = 0
          }
        })
        .catch(error => {
          console.error('获取填报模板列表失败:', error)
          this.$message.error('获取填报模板列表失败')
          this.tableData = []
          this.pagination.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    // 新增填报
    handleAddReport() {
      this.$router.push({ path: '/system/report-edit' })
    },
    
    // 编辑填报
    handleEdit(row) {
      this.$router.push({
        path: '/system/report-edit',
        query: { id: row.id }
      })
    },
    
    // 切换状态
    handleToggleStatus(row) {
      // 状态切换：1启用 -> 2禁用，2禁用 -> 1启用
      const newStatus = row.status === 1 ? 2 : 1
      const statusName = newStatus === 1 ? '启用' : '禁用'
      
      this.$confirm(`确定要${statusName}该填报模板吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        updateReportTemplateStatus(row.id, newStatus)
          .then(response => {
            if (response.code === 200) {
              row.status = newStatus
              row.statusName = statusName
              this.$message.success(`${statusName}成功`)
            } else {
              this.$message.error(response.message || `${statusName}失败`)
            }
          })
          .catch(error => {
            console.error(`${statusName}填报模板失败:`, error)
            this.$message.error(`${statusName}失败`)
          })
      }).catch(() => {
        // 取消操作
      })
    },
    
    // 删除填报
    handleDelete(row) {
      this.currentDeleteId = row.id
      this.deleteDialogVisible = true
    },
    
    // 确认删除
    confirmDelete() {
      this.loading = true
      deleteReportTemplate(this.currentDeleteId)
        .then(response => {
          if (response.code === 200) {
            this.$message.success('删除成功')
            // 删除成功后刷新列表
            this.fetchReportTemplates()
          } else {
            this.$message.error(response.message || '删除失败')
          }
        })
        .catch(error => {
          console.error('删除填报模板失败:', error)
        })
        .finally(() => {
          this.loading = false
          this.deleteDialogVisible = false
        })
    },
    
    // 关闭弹窗
    handleCloseDialog() {
      this.dialogVisible = false
    },
    
    // 提交表单
    submitForm() {
      this.$refs.reportForm.validate((valid) => {
        if (valid) {
          if (this.dialogType === 'add') {
            // 模拟添加数据
            const newItem = {
              id: Date.now(),
              index: this.tableData.length + 1,
              templateName: this.formData.templateName,
              healthStatus: this.formData.healthStatus,
              applicableTeams: this.formData.applicableTeams.join('、'),
              reportMethod: this.formData.reportMethod,
              status: this.formData.status,
              lastModifier: '张三',
              lastModifiedTime: new Date().toLocaleString()
            };
            this.tableData.push(newItem);
            this.pagination.total = this.tableData.length;
            this.$message.success('添加成功');
          } else {
            // 模拟编辑数据
            const index = this.tableData.findIndex(item => item.id === this.formData.id);
            if (index !== -1) {
              this.tableData[index] = {
                ...this.tableData[index],
                templateName: this.formData.templateName,
                healthStatus: this.formData.healthStatus,
                applicableTeams: this.formData.applicableTeams.join('、'),
                reportMethod: this.formData.reportMethod,
                status: this.formData.status,
                lastModifier: '张三',
                lastModifiedTime: new Date().toLocaleString()
              };
              this.$message.success('编辑成功');
            }
          }
          this.dialogVisible = false;
        } else {
          return false;
        }
      });
    },
    
    // 分页大小变化
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1 // 重置为第一页
      this.fetchReportTemplates()
    },
    
    // 当前页变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.fetchReportTemplates()
    },
    
    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1 // 重置为第一页
      this.fetchReportTemplates()
    },
    
    // 重置搜索
    resetSearch() {
      this.filters.name = ''
      this.pagination.currentPage = 1
      this.fetchReportTemplates()
    }
  }
}
</script>

<style scoped>
.report-management-container {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #303133;
}

/* 筛选区域样式 */
.filter-section {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
}

.filter-input {
  width: 300px;
}

.spacer {
  flex-grow: 1;
}

/* 表格状态样式 */
.status-normal {
  color: #67C23A;
}

.status-enabled {
  color: #67C23A;
}

.status-disabled {
  color: #909399;
}

.delete-btn {
  color: #F56C6C;
}

/* 分页容器 */
.pagination-container {
  margin-top: 20px;
  text-align: right;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .filter-input {
    width: 100%;
    margin-bottom: 10px;
  }
  
  .spacer {
    display: none;
  }
}
</style> 